/**
 * Locale foundation for StudyLabPro.
 *
 * The site is one route tree under /[lang]. English is the default and both
 * locales are prefixed (/en, /ru); the bare root redirects to /en. Content
 * lives in English registries with Russian overlays (see src/content/i18n.ts),
 * so slugs, ids and the AI action protocol stay language-agnostic — only the
 * rendered strings and the URL prefix change.
 */

export const LOCALES = ['en', 'ru'] as const;
export type Lang = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Lang = 'en';

export function isLang(v: string | undefined): v is Lang {
  return v === 'en' || v === 'ru';
}

export function otherLang(lang: Lang): Lang {
  return lang === 'en' ? 'ru' : 'en';
}

/** BCP-47 value for <html lang>. */
export const HTML_LANG: Record<Lang, string> = { en: 'en', ru: 'ru' };

/** OpenGraph locale. */
export const OG_LOCALE: Record<Lang, string> = { en: 'en_US', ru: 'ru_RU' };

/** Human name of each locale, in its own language (for the switcher). */
export const LOCALE_NAME: Record<Lang, string> = { en: 'EN', ru: 'RU' };

/**
 * Prefix a logical path with a locale.
 * `localePath('ru', '/research')` → `/ru/research`; `localePath('en', '/')` → `/en`.
 */
export function localePath(lang: Lang, path: string): string {
  if (!path || path === '/') return `/${lang}`;
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `/${lang}${clean}`;
}

/** Strip a leading locale segment, returning the logical path (`/research`, `/`). */
export function stripLocale(pathname: string): string {
  const m = pathname.match(/^\/(en|ru)(\/.*)?$/);
  if (!m) return pathname || '/';
  return m[2] && m[2] !== '' ? m[2] : '/';
}

/** Detect the active locale from a pathname (defaults to DEFAULT_LOCALE). */
export function langFromPathname(pathname: string): Lang {
  const seg = pathname.split('/')[1];
  return isLang(seg) ? seg : DEFAULT_LOCALE;
}

/** The same page in the other locale — for the language switcher. */
export function swapLocale(pathname: string, toLang: Lang): string {
  return localePath(toLang, stripLocale(pathname));
}
